import { ApiResponse } from '@ametista-ai/api-schema/http/types';
import { startAuthentication } from '@simplewebauthn/browser';
import invariant from 'invariant';
import { apiClient } from '@/stores/api/apiClient';

/**
 * One-shot auth actions with no reactive state of their own — callers track
 * their own loading/error state locally, so these are plain functions
 * rather than React Query hooks.
 */

export async function startGoogleSignIn() {
  const baseUrl = window.location.origin;
  const response = await apiClient.get<ApiResponse<{ authUrl: string }>>('/v1/auth/signin/google', {
    baseUrl,
  });
  invariant(response?.data, 'Data not available');
  window.location.href = response.data.authUrl;
}

export async function signInGoogle({ code }: { code: string }) {
  const baseUrl = window.location.origin;
  const response = await apiClient.post<ApiResponse<{ success: boolean }>>(
    '/v1/auth/signin/google',
    { code, baseUrl }
  );
  invariant(response?.data, 'Data not available');
  return response.data.success;
}

export async function signOut() {
  await apiClient.post<ApiResponse<{ success: boolean }>>('/v1/auth/signout', {});
}

export async function startPasskeySignIn(email: string) {
  const res = await apiClient.post<ApiResponse<{ options: any; origin: string }>>(
    '/v1/auth/signin/passkey/options',
    { email, flow: 'authenticate' }
  );
  invariant(res?.data, 'Data not available');
  const assertion = await startAuthentication({ optionsJSON: res.data.options });
  const verify = await apiClient.post<ApiResponse<{ success: boolean }>>(
    '/v1/auth/signin/passkey/verify',
    {
      email,
      response: assertion,
      expectedChallenge: res.data.options.challenge,
    }
  );
  invariant(verify?.data, 'Data not available');
  return verify.data.success;
}

/**
 * Drives the login page's discoverable-credential ("conditional UI") flow:
 * no email needed upfront, the browser offers any resident passkey saved
 * for this site via its native autofill UI as soon as the page loads.
 * `startAuthentication`'s `useBrowserAutofill` requires an `<input>` with
 * `autoComplete="username webauthn"` already mounted, and its returned
 * promise only settles once the user picks a credential (or the ceremony
 * is aborted — e.g. by starting a different sign-in flow in the
 * meantime, which SimpleWebAuthn's browser package cancels automatically).
 */
export async function startConditionalPasskeySignIn() {
  const res = await apiClient.post<ApiResponse<{ options: any; origin: string }>>(
    '/v1/auth/signin/passkey/options',
    { flow: 'authenticate' }
  );
  invariant(res?.data, 'Data not available');
  const assertion = await startAuthentication({
    optionsJSON: res.data.options,
    useBrowserAutofill: true,
  });
  const verify = await apiClient.post<ApiResponse<{ success: boolean }>>(
    '/v1/auth/signin/passkey/verify',
    { response: assertion }
  );
  invariant(verify?.data, 'Data not available');
  return verify.data.success;
}

export async function ensureCdnCookies() {
  await apiClient.get('/v1/me/cookies', {});
}
